import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button } from '../../components/Button';
import { API_URL } from '../../api/client';

interface StaffUser {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  role: string;
  phone: string;
  department: string;
  employee_id: string;
  permissions: string[];
  last_login: string;
  created_at: string;
  updated_at: string;
}

interface Role {
  role: string;
  label: string;
  description: string;
  permissions: string[];
}

export const StaffDetails: React.FC = () => {
  const { staffId } = useParams<{ staffId: string }>();
  const navigate = useNavigate();
  const [staff, setStaff] = useState<StaffUser | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    role: '',
    department: '',
    employee_id: '',
    status: ''
  });

  useEffect(() => {
    fetchStaffDetails();
    fetchRoles();
  }, [staffId]);

  const fetchStaffDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/admin/staff/${staffId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch staff details');
      }

      const data = await response.json();
      setStaff(data.data);
      setFormData({
        first_name: data.data.first_name,
        last_name: data.data.last_name,
        phone: data.data.phone || '',
        role: data.data.role,
        department: data.data.department || '',
        employee_id: data.data.employee_id || '',
        status: data.data.status
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load staff details');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/staff/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch roles');
      }

      const data = await response.json();
      setRoles(data.data.roles);
    } catch (err: any) {
      console.error('Failed to fetch roles:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/admin/staff/${staffId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update staff user');
      }

      setSuccessMessage('Staff user updated successfully');
      setIsEditing(false);
      await fetchStaffDetails();
    } catch (err: any) {
      setError(err.message || 'Failed to update staff user');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/admin/staff/${staffId}/password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ new_password: newPassword }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update password');
      }

      setSuccessMessage('Password updated successfully');
      setShowPasswordModal(false);
      setNewPassword('');
    } catch (err: any) {
      setError(err.message || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Are you sure you want to deactivate this staff user?')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/admin/staff/${staffId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to deactivate staff user');
      }

      setSuccessMessage('Staff user deactivated successfully');
      navigate('/admin/staff');
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate staff user');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleLabel = (role: string) => {
    const roleObj = roles.find(r => r.role === role);
    return roleObj ? roleObj.label : role;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error && !staff) {
    return <ErrorMessage message={error} />;
  }

  if (!staff) {
    return <ErrorMessage message="Staff user not found" />;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <Button
            onClick={() => navigate('/admin/staff')}
            variant="ghost"
          >
            ← Back to Staff List
          </Button>
          <h1>{staff.first_name} {staff.last_name}</h1>
          <p className="page-description">{staff.email}</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          {isEditing ? (
            <>
              <Button
                onClick={() => setIsEditing(false)}
                variant="secondary"
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="primary"
                disabled={saving}
                loading={saving}
              >
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => setShowPasswordModal(true)}
                variant="secondary"
              >
                Reset Password
              </Button>
              <Button
                onClick={() => setIsEditing(true)}
                variant="primary"
              >
                Edit Details
              </Button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          {successMessage}
        </div>
      )}

      <div className="details-grid">
        {/* Basic Information */}
        <div className="details-card">
          <h2>Basic Information</h2>
          {isEditing ? (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="input"
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="input"
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input"
                />
              </div>
              <div className="form-group">
                <label>Employee ID</label>
                <input
                  type="text"
                  name="employee_id"
                  value={formData.employee_id}
                  onChange={handleChange}
                  className="input"
                />
              </div>
              <div className="form-group">
                <label>Department</label>
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  className="input"
                />
              </div>
            </form>
          ) : (
            <div className="details-list">
              <div className="detail-item">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{staff.email}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Phone:</span>
                <span className="detail-value">{staff.phone || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Employee ID:</span>
                <span className="detail-value">{staff.employee_id || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Department:</span>
                <span className="detail-value">{staff.department || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Last Login:</span>
                <span className="detail-value">{formatDate(staff.last_login)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Created:</span>
                <span className="detail-value">{formatDate(staff.created_at)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Role & Permissions */}
        <div className="details-card">
          <h2>Role & Permissions</h2>
          {isEditing ? (
            <div className="form-group">
              <label>Role</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="select"
              >
                {roles.map((role) => (
                  <option key={role.role} value={role.role}>
                    {role.label}
                  </option>
                ))}
              </select>
              {roles.find(r => r.role === formData.role) && (
                <div className="role-info">
                  <p>{roles.find(r => r.role === formData.role)?.description}</p>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="detail-item">
                <span className="detail-label">Role:</span>
                <span className={`badge badge-primary`}>
                  {getRoleLabel(staff.role)}
                </span>
              </div>
              <div className="permissions-section">
                <h3>Permissions</h3>
                <ul className="permissions-list">
                  {staff.permissions && JSON.parse(staff.permissions as any).map((permission: string) => (
                    <li key={permission}>✓ {permission.replace(/_/g, ' ')}</li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>

        {/* Account Status */}
        <div className="details-card">
          <h2>Account Status</h2>
          {isEditing ? (
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="select"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          ) : (
            <div className="details-list">
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <span className={`badge ${
                  staff.status === 'active' ? 'badge-success' :
                  staff.status === 'inactive' ? 'badge-secondary' :
                  'badge-warning'
                }`}>
                  {staff.status}
                </span>
              </div>
            </div>
          )}

          <div className="danger-zone">
            <h3>Danger Zone</h3>
            <p>Deactivating this staff user will revoke their access immediately.</p>
            <Button
              onClick={handleDeactivate}
              variant="danger"
              disabled={saving || staff.status === 'inactive'}
            >
              Deactivate Staff User
            </Button>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reset Password</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowPasswordModal(false)}>×</Button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input"
                  placeholder="Enter new password (min 8 characters)"
                  minLength={8}
                />
              </div>
            </div>
            <div className="modal-footer">
              <Button
                onClick={() => setShowPasswordModal(false)}
                variant="secondary"
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePasswordChange}
                variant="primary"
                disabled={saving || newPassword.length < 8}
                loading={saving}
              >
                Update Password
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .back-button {
          background: none;
          border: none;
          color: #2563eb;
          cursor: pointer;
          font-size: 14px;
          margin-bottom: 12px;
          padding: 0;
        }

        .back-button:hover {
          text-decoration: underline;
        }

        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 24px;
        }

        .details-card {
          background: white;
          border-radius: 8px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .details-card h2 {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 20px 0;
          color: #1f2937;
        }

        .details-card h3 {
          font-size: 16px;
          font-weight: 600;
          margin: 20px 0 12px 0;
          color: #374151;
        }

        .details-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 12px;
          border-bottom: 1px solid #e5e7eb;
        }

        .detail-item:last-child {
          border-bottom: none;
        }

        .detail-label {
          font-weight: 500;
          color: #6b7280;
        }

        .detail-value {
          color: #1f2937;
          font-weight: 500;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          margin-bottom: 8px;
        }

        .role-info {
          margin-top: 12px;
          padding: 12px;
          background: #f9fafb;
          border-radius: 6px;
          font-size: 14px;
          color: #6b7280;
        }

        .permissions-section {
          margin-top: 20px;
        }

        .permissions-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .permissions-list li {
          padding: 8px 0;
          color: #374151;
          font-size: 14px;
          text-transform: capitalize;
        }

        .danger-zone {
          margin-top: 32px;
          padding-top: 24px;
          border-top: 2px solid #fee2e2;
        }

        .danger-zone h3 {
          color: #dc2626;
          margin-bottom: 8px;
        }

        .danger-zone p {
          font-size: 14px;
          color: #6b7280;
          margin-bottom: 16px;
        }

        .alert {
          padding: 12px 16px;
          border-radius: 6px;
          margin-bottom: 20px;
        }

        .alert-error {
          background: #fef2f2;
          color: #991b1b;
          border: 1px solid #fee2e2;
        }

        .alert-success {
          background: #f0fdf4;
          color: #166534;
          border: 1px solid #bbf7d0;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 500px;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 20px;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 32px;
          color: #9ca3af;
          cursor: pointer;
          line-height: 1;
          padding: 0;
        }

        .modal-body {
          padding: 24px;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 24px;
          border-top: 1px solid #e5e7eb;
        }
      `}</style>
    </div>
  );
};
